import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, CustomSelect, TextArea, TextInput } from "../../components";
import {
  addBusiness,
  updateBusiness,
} from "@/store/constants/constants.action";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../store";
import ImageUpload from "../ImageUpload/ImageUpload";
import { callRegisterMediaOnCms } from "@/store/upload/upload.action";
import { setCurrentBusiness } from "@/store/constants/constants.slice";
import Search from "../../assets/icons/svg/search.svg?react";
import { toast } from "../../components";
import SidebarNavigation, {
  SidebarMenuItem,
} from "@/components/SidebarNavigation/SidebarNavigation";
import { User } from "lucide-react";
import {
  updateIsClearFilter,
  updateIsFilter,
} from "@/store/media/media.action";
import { getListUser } from "@/store/user/user.action";
import { truncateText } from "@/utils/utilFunctions/truncateText";
import { useDebounce } from "../../utils";

interface AddBusinessFormProps {
  onClose: () => void;
  business?: any;
  onFormStateChange?: (state: {
    isDirty: boolean;
    isValid: boolean;
    isLoading: { error: boolean; success: boolean; loading: boolean };
  }) => void;
}

export const AddBusinessForm = ({
  onClose,
  addBusiness,
  updateBusiness,
  callRegisterMediaOnCms,
  setCurrentBusiness,
  businessList,
  currentBusiness,
  onFormStateChange,
  updateIsFilter,
  updateIsClearFilter,
  getListUser,
}: Props) => {
  const [businessId, setBusinessId] = useState<string>("");
  const callback = useRef<((query: any, token: any) => void) | null>(null);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [isLoading, setIsLoading] = useState({
    error: false,
    success: false,
    loading: false,
  });
  const [logoUpdated, setLogoUpdated] = useState(true);
  const [activeSection, setActiveSection] = useState<string>("basic-info");

  const [approversList, setApproversList] = useState<any[]>([]);
  const [selectedApprovers, setSelectedApprovers] = useState<any[]>([]);
  const [approverSearchKeyword, setApproverSearchKeyword] =
    useState<string>("");
  const debouncedApproverKeyword = useDebounce(approverSearchKeyword, 200);

  useEffect(() => {
    const approvers = currentBusiness?.userdata?.map((approver: any) => ({
      label: ` ${approver?.name}  ${
        approver?.email ? "(" + truncateText(approver?.email, 50) + ")" : ""
      } `,
      value: approver?.id,
    }));

    if (currentBusiness) {
      setSelectedApprovers(approvers);
    }
  }, [currentBusiness]);

  useEffect(() => {
    if (debouncedApproverKeyword.length >= 3) {
      getListUser?.({ name: debouncedApproverKeyword, setInRedux: false }).then(
        (res: any) => {
          setApproversList(res || []);
        }
      );
    } else {
      setApproversList([]);
    }
  }, [debouncedApproverKeyword, getListUser]);

  // Refs for sections
  const basicInfoRef = useRef<HTMLDivElement>(null);

  // Define sidebar menu items
  const sidebarMenuItems: SidebarMenuItem[] = [
    {
      id: "basic-info",
      label: "Basic Information",
      icon: User,
      sectionRef: basicInfoRef,
    },
  ];

  useEffect(() => {
    if (isLoading.success || !logoUpdated) {
      onClose();
    }
  }, [isLoading, logoUpdated]);

  const handleApiError = (error: any) => {
    if (typeof error === "string") {
      const fieldErrors: Record<string, string> = {};
      if (error?.toLowerCase().includes("unique constraint failed")) {
        toast.fail(
          "Business Name already exists. Please enter a different name"
        );
        return;
      } else if (error.toLowerCase().includes("name")) {
        fieldErrors.name = error;
      } else if (error.toLowerCase().includes("code")) {
        fieldErrors.code = error;
      }
      if (Object.keys(fieldErrors).length > 0) {
        formik.setErrors(fieldErrors);
      }
    } else {
      toast.fail("");
    }
  };

  const formik = useFormik({
    validateOnChange: true,
    initialValues: {
      name: currentBusiness?.name || "",
      display_name: currentBusiness?.display_name || "",
      desc: currentBusiness?.desc || "",
      status: currentBusiness?.archive ? 2 : 1,
      approvers: currentBusiness?.approvers || [],
      logo: currentBusiness?.logo || "",
      code: currentBusiness?.code || "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Business Name is required")
        .min(3, "Business Name must be more than 3 characters"),
      approvers: Yup.array()
        .min(1, "At least one approver is required")
        .required("Approvers are required"),
      code: Yup.string()
        .required("Business Code is required")
        .min(3, "Business Code must be more than 3 characters"),
    }),

    onSubmit: async (values) => {
      try {
        const onSuccess = (response: any) => {
          const newBusinessList = [response, ...businessList];
          const businessId = response.id;
          setBusinessId(businessId);
          uploadLogo(response.id, newBusinessList);
          const successMessage = currentBusiness
            ? "Business updated successfully"
            : "Business created successfully";
          toast.success(successMessage);
        };

        const uploadLogo = (id: string, bList: any) => {
          const businessId = currentBusiness ? currentBusiness?.id : id;
          if (selectedImage && callback.current != null) {
            setLogoUpdated(true);
            callRegisterMediaOnCms(selectedImage, (response: any) => {
              if (response && response.status === 200) {
                const token = response.data.jwt;
                const query = {
                  entity: businessId,
                  key: "logo",
                };
                callback.current && callback.current(query, token);
                const updatedBusiness = bList.filter(
                  (business: any) => business.id === businessId
                );
                const ub = {
                  ...updatedBusiness[0],
                  logo: response.data.AssetID,
                };
                setCurrentBusiness(ub);
              } else {
                handleApiError(response.error);
                toast.fail("");
              }
            });
          } else {
            setLogoUpdated(false);
          }
        };
        if (currentBusiness) {
          const { status, ...restValues } = values;
          const params = {
            ...restValues,
            // archive: values.status === 2 ? 1 : 0,
            id: currentBusiness?.id,
          };
          await updateBusiness(params, onSuccess, handleApiError);
        } else {
          updateIsClearFilter(true);
          updateIsFilter(false);
          await addBusiness(
            { ...values, archive: values.status === 2 ? 1 : 0 },
            onSuccess,
            handleApiError
          );
        }
      } catch (error) {
        handleApiError(error);
      }
    },
  });

  const handleChange = (values: string[]) => {
    const newApprovers = values.map((value) => {
      const existingApprover = formik.values.approvers.find(
        (approver: string) => approver.startsWith(value)
      );
      return existingApprover ? existingApprover : value;
    });
    formik.setFieldValue("approvers", newApprovers);
  };

  useEffect(() => {
    onFormStateChange?.({
      isDirty: formik.dirty,
      isValid: formik.isValid,
      isLoading: isLoading,
    });
  }, [formik.dirty, formik.isValid, isLoading, onFormStateChange]);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();
    formik.setTouched({
      name: true,
      approvers: true,
      code: true,
    });
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }
    await formik.handleSubmit();
  };

  useEffect(() => {
    const handleSubmit = (e: any) => {
      e.preventDefault();
      handleFormSubmit(e);
    };
    window.addEventListener("submit", handleSubmit);
    return () => {
      window.removeEventListener("submit", handleSubmit);
    };
  }, [handleFormSubmit]);

  return (
    <div className="mx-2 my-3 row">
      <SidebarNavigation
        className="col-sm-5 col-md-4 col-lg-3 sticky-top sidebar-top"
        menuItems={sidebarMenuItems}
        activeItem={activeSection}
        onItemClick={setActiveSection}
      />
      <form
        className="col-sm-7 col-md-8 col-lg-9 gap-3"
        id="businessForm"
        onSubmit={async (e) => {
          e.preventDefault();
          formik.setTouched({
            name: true,
            approvers: true,
            code: true,
          });
          const errors = await formik.validateForm();
          if (Object.keys(errors).length > 0) {
            return;
          }
          await formik.handleSubmit();
        }}
      >
        <div ref={basicInfoRef} className="card mx-0 mb-4 pb-3">
          <div className="card-header py-3 bg-light border-0">
            <h6 className="text-uppercase">Basic Information</h6>
          </div>
          <div className="row p-2 gy-2 m-0">
            <div className="col-12">
              <div className="row m-0 gy-2">
                <div className="col-md-2 col-lg-3 ps-0">
                  <ImageUpload
                    id={businessId}
                    registerMedia={callRegisterMediaOnCms}
                    setSelectedImage={(image: File | null) => {
                      setSelectedImage(image);
                      formik.setFieldValue("logo", Date.now().toString());
                    }}
                    callbackRef={callback}
                    type="business"
                    logo={
                      currentBusiness?.logo
                        ? `${currentBusiness.baseurl}/${currentBusiness.logo}.jpg`
                        : ""
                    }
                    hasBaseUrl={currentBusiness?.baseurl}
                    setIsLoading={setIsLoading}
                  />
                </div>
                <div className="col-md-10 col-lg-9 row m-0 gy-2 pe-0">
                  <div className="col-12 p-0">
                    <TextInput
                      name="name"
                      type="text"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      showRequired={true}
                      label="Title"
                      error={formik.errors.name as string}
                      touched={formik.touched.name as boolean}
                    />
                  </div>

                  <div className="col-12 p-0">
                    <TextInput
                      name="display_name"
                      type="text"
                      value={formik.values.display_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      showRequired={false}
                      label="Display Name"
                      placeholder="Enter Display Name"
                    />
                  </div>

                  <div className="col-md-12 p-0 H">
                    <CustomSelect
                      className="mb-3"
                      value={formik.values.status}
                      label="Status"
                      name="status"
                      options={[
                        { label: "Active", value: 1 },
                        { label: "Inactive", value: 2 },
                      ]}
                      disabled={false}
                      placeholder="Select Status"
                      handleChange={(newValue: any) => {
                        formik.setFieldValue("status", newValue.value);
                      }}
                    />
                  </div>
                  <div className="col-md-12 p-0">
                    <TextInput
                      className="mb-3"
                      value={formik.values.code}
                      label="Code"
                      name="code"
                      placeholder="Enter Code"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      showRequired={true}
                      disabled={currentBusiness}
                      error={formik.errors.code as string}
                      touched={formik.touched.code as boolean}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <TextArea
                name="desc"
                value={formik.values.desc}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Description"
                showRequired={false}
              />
            </div>
            <div className="col-12">
              <CustomSelect
                className="mb-3"
                value={formik.values.approvers}
                label="Approvers"
                name="approvers"
                loader={true}
                // options={
                //   userListOptions?.map((user: any) => ({
                //     label: ` ${user?.name}  ${user?.email ? '(' + truncateText(user?.email, 50) + ')' : ''} `,
                //     value: user.id,
                //   })) || []
                // }
                options={approversList?.map((user: any) => ({
                  label: ` ${user?.name}  ${
                    user?.email ? "(" + truncateText(user?.email, 50) + ")" : ""
                  } `,
                  value: user.id,
                }))}
                multiSelect={true}
                onInputChangeCallback={(params) => {
                  setApproverSearchKeyword(params.keyword);
                }}
                disabled={false}
                showRequired={true}
                placeholder={
                  <div className="d-flex align-items-center gap-1">
                    <Search style={{ width: "14px", height: "14px" }} />
                    Search Approvers
                  </div>
                }
                handleChange={(newValue: any) => {
                  handleChange(newValue.map((option: any) => option.value));
                }}
                error={formik.errors.approvers as string}
                touched={formik.touched.approvers as boolean}
                // updatedOptions={
                //   userListOptions?.map((user: any) => ({
                //     label: ` ${user?.name}  ${user?.email ? '(' + truncateText(user?.email, 50) + ')' : ''} `,
                //     value: user.id,
                //   })) || []
                // }
                updatedOptions={approversList?.map((user: any) => ({
                  label: ` ${user?.name}  ${
                    user?.email ? "(" + truncateText(user?.email, 50) + ")" : ""
                  } `,
                  value: user.id,
                }))}
                creditData={selectedApprovers}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

const mapState = (state: RootState) => ({
  businessList: state.constants.businessList.list,
  currentBusiness: state.constants.currentBusiness,
  userListOptions: state.users.userList.list,
});
const mapDispatch = {
  addBusiness,
  updateBusiness,
  callRegisterMediaOnCms,
  setCurrentBusiness,
  updateIsFilter,
  updateIsClearFilter,
  getListUser,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & AddBusinessFormProps;

export default connector(AddBusinessForm);
